import * as React from 'react';
import * as PopoverPrimitive from '@radix-ui/react-popover';

import { ButtonGroupContext } from './ButtonGroup';
import { cn } from './cn';
import { useUiPortalContainer } from '@/js/wisemetering/ui/render_with_ui_root';
import { WM_FLOATING_SURFACE_CLASSNAME } from './surfaceStyles';

// Popover — click-anchored floating panel.
//
// Closed shape:
//
//   <Popover
//       trigger={<Button>Open</Button>}
//       content={<div>...rich content...</div>}
//       open={open}                 // optional, controlled state
//       onOpenChange={setOpen}      // optional
//       align="start"               // optional, default 'center'
//       side="bottom"               // optional, default 'bottom'
//       sideOffset={4}              // optional, default 4
//       alignOffset={0}             // optional, default 0
//       noPadding={false}           // optional, default false
//       onOpenAutoFocus={...}       // optional, Radix focus events
//       onCloseAutoFocus={...}      // optional
//   />
//
// `trigger` and `content` are both ReactNode — symmetric since both
// slots carry substantive UI (vs Tooltip where content is a string
// and the trigger reads as the primary element). When the JSX gets
// rich, the caller naturally extracts `renderTrigger()` /
// `renderContent()` helpers and the call-site stays flat.
//
// The popover paints only its outer shell (border, rounded, shadow,
// background). Internal padding is the caller's job — most real
// popover content (calendars, pickers, search lists, color
// swatches) paints its own layout edge-to-edge, so a baked-in
// padding would mean every callsite reaches for an escape hatch.
// "Quick info" popovers add their own `tw-p-4` wrapper.
//
// `preventAutoFocus` is a single boolean over Radix's two focus
// events: when set, both `onOpenAutoFocus` and `onCloseAutoFocus`
// are short-circuited with `event.preventDefault()`. Auto-focus is
// on by default because it's the a11y-correct behaviour: keyboard
// users and screen readers need focus to enter the new panel. Some
// callsites (Calendar) own visual selection and prefer focus to
// stay put — that's what this prop is for. If granular control
// shows up as a real need (e.g., "focus a specific element on open"),
// re-expose the raw Radix events at that point.
//
// React context propagates through portals, so any
// `ButtonGroupContext` from whatever the trigger sits inside would
// leak into the popover content. We null it out at the boundary —
// the popover panel is its own composition space.

const POPOVER_CONTENT_ANIMATION_CLASSNAME = 'data-[state=open]:tw-animate-in data-[state=closed]:tw-animate-out data-[state=closed]:tw-fade-out-0 data-[state=open]:tw-fade-in-0 data-[state=closed]:tw-zoom-out-95 data-[state=open]:tw-zoom-in-95 data-[side=bottom]:tw-slide-in-from-top-2 data-[side=left]:tw-slide-in-from-right-2 data-[side=right]:tw-slide-in-from-left-2 data-[side=top]:tw-slide-in-from-bottom-2';

function Popover({
    trigger,
    content,
    open,
    onOpenChange,
    align = 'center',
    side = 'bottom',
    sideOffset = 4,
    alignOffset = 0,
    matchTriggerWidth = false,
    preventAutoFocus = false
}) {
    const portalContainer = useUiPortalContainer();
    const handleAutoFocus = preventAutoFocus
        ? event => event.preventDefault()
        : undefined;

    return (
        <PopoverPrimitive.Root open={open} onOpenChange={onOpenChange}>
            <PopoverPrimitive.Trigger asChild>{trigger}</PopoverPrimitive.Trigger>
            <PopoverPrimitive.Portal container={portalContainer ?? undefined}>
                <PopoverPrimitive.Content
                    align={align}
                    side={side}
                    sideOffset={sideOffset}
                    alignOffset={alignOffset}
                    onOpenAutoFocus={handleAutoFocus}
                    onCloseAutoFocus={handleAutoFocus}
                    className={cn(
                        WM_FLOATING_SURFACE_CLASSNAME,
                        'tw-z-50 tw-rounded-xl tw-text-foreground tw-outline-none',
                        // `matchTriggerWidth` opts into the Radix-provided
                        // `--radix-popover-trigger-width` CSS variable so the
                        // popover paints exactly as wide as the anchor — the
                        // combobox / typeahead pattern, e.g. ComparisonSection
                        // in the indicator plot. Default keeps the
                        // select/menu-style 288px floor (`min-w-72`) and lets
                        // wider content (Calendar range = 640px) drive the
                        // surface size.
                        matchTriggerWidth
                            ? 'tw-w-[var(--radix-popover-trigger-width)]'
                            : 'tw-min-w-72',
                        POPOVER_CONTENT_ANIMATION_CLASSNAME
                    )}
                >
                    <ButtonGroupContext.Provider value={null}>
                        {content}
                    </ButtonGroupContext.Provider>
                </PopoverPrimitive.Content>
            </PopoverPrimitive.Portal>
        </PopoverPrimitive.Root>
    );
}

export { Popover };
